/**
 * KRA Photo Journal – TagChip
 *
 * Small pill chip for displaying a single tag value.
 * Variants:
 *   - default: subtle background, for display in cards/modal
 *   - active:  highlighted, for active filter state
 *   - filter:  clickable, for filter panel options
 */

import React from "react";
import { cn } from "../lib/utils.js";

interface TagChipProps {
  label: string;
  variant?: "default" | "active" | "filter";
  onClick?: (e: React.MouseEvent) => void;
  onRemove?: () => void;
  className?: string;
  /** Shows pointer cursor + hover highlight even without onClick */
  clickable?: boolean;
}

export function TagChip({ label, variant = "default", onClick, onRemove, className, clickable }: TagChipProps) {
  const isInteractive = Boolean(onClick || clickable);
  return (
    <span
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={isInteractive && onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e as unknown as React.MouseEvent); } } : undefined}
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium",
        "leading-none select-none whitespace-nowrap",
        variant === "active"
          ? "bg-white/20 text-white"
          : variant === "filter"
          ? "bg-surface-2 text-ink-1 hover:bg-surface-3 cursor-pointer transition-colors"
          : "bg-surface-2/60 text-ink-2",
        isInteractive && "cursor-pointer hover:bg-white/15 hover:text-ink-0 transition-colors",
        className
      )}
    >
      {label}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="ml-0.5 text-current opacity-60 hover:opacity-100 transition-opacity leading-none"
          aria-label={`Remove ${label}`}
        >
          ×
        </button>
      )}
    </span>
  );
}

/** Overflow chip: "+N more" */
export function TagOverflowChip({ count }: { count: number }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface-2/40 text-ink-3 whitespace-nowrap leading-none">
      +{count}
    </span>
  );
}
